import { Component, OnDestroy } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs/Subscription';

import { AdminService } from '../../../../services/admin.service';
import {
  AdminSalaryUpdateRequest,
  CurrencyCode,
  CurrencyCodes,
  PAYROLL_CURRENCY_CODES,
  SalaryContext,
  MonthlyBonus,
  trackByCurrencyCode
} from '../../../../models/payroll-currency.model';
import { AppConstants } from '../app.constants';
import {payrollNumberDisplay} from 'app/models/payroll-number-display';
import {salaryDescriptionText} from 'app/models/salary-description';

const MAX_SALARY = 999999;
const MAX_BONUS_AMOUNT = 999999.99;
const ZERO_AMOUNT = 0;
const FIRST_BONUS_MONTH = 1;
const MAX_BONUS_MONTHS = 1200;
const BONUS_MONEY_FACTOR = 100;
const BONUS_CONFLICT_STATUS = 409;
const BONUS_CONFLICT_KEY = 'PAYROLL_BONUS_STALE';
const BONUS_SAVE_ERROR_KEY = 'PAYROLL_BONUS_SAVE_ERROR';
const NUMBER_TYPE = 'number';
const EMPTY_DESCRIPTION = '';

@Component({
  selector: 'app-change-salary',
  templateUrl: './change-salary.component.html',
  styleUrls: ['./change-salary.component.css']
})
export class ChangeSalaryComponent implements OnDestroy {
  public id: string;
  public value: number;
  public changeLog = '';
  public effectiveCurrency: CurrencyCode = CurrencyCodes.BYN;
  public inheritedCurrency: CurrencyCode = CurrencyCodes.BYN;
  public selectedCurrency: CurrencyCode = CurrencyCodes.BYN;
  public readonly payrollCurrencies = PAYROLL_CURRENCY_CODES;
  public readonly trackByCurrency = trackByCurrencyCode;
  public inheritCalendarCurrency = true;
  public isAdmin = false;
  public isLoading = false;
  public salaryContextLoaded = false;
  public disabledBtn = false;
  public errorInfo = false;
  public bonus: MonthlyBonus;
  public bonusForever = true;
  public bonusErrorKey: string = null;
  public salaryEditing = false;
  private initialBonus: MonthlyBonus;

  private readonly subscriptions = new Subscription();

  constructor(
    public activeModal: NgbActiveModal,
    private adminService: AdminService
  ) {}

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public setId(id: string): void {
    this.id = id;
    this.loadSalaryContext();
  }

  public setValue(value: number): void {
    this.value = value;
  }

  public setAdmin(isAdmin: boolean): void {
    this.isAdmin = isAdmin;
  }

  public change(value: number): void {
    if (value > MAX_SALARY) {
      this.value = MAX_SALARY;
    }
    if (value < 0) {
      this.value = 0;
    }
  }

  public save(): void {
    if (this.saveDisabled) {
      return;
    }
    this.disabledBtn = true;
    this.errorInfo = false;
    this.bonusErrorKey = null;
    if (this.isAdmin) {
      this.saveAsAdmin();
      return;
    }
    this.saveAsHead();
  }

  public get saveDisabled(): boolean {
    return this.isLoading
      || !this.salaryContextLoaded
      || this.disabledBtn
      || !!this.bonus && this.bonusSaveDisabled
      || typeof this.value !== NUMBER_TYPE
      || !isFinite(this.value)
      || this.value < ZERO_AMOUNT
      || this.value > MAX_SALARY;
  }

  public get salaryCurrency(): CurrencyCode {
    return this.inheritCalendarCurrency ? this.inheritedCurrency : this.selectedCurrency;
  }

  public get displayedSalary(): string | number {
    return this.salaryEditing || this.value == null ? this.value : payrollNumberDisplay(this.value, true);
  }

  public get bonusSaveDisabled(): boolean {
    return this.isLoading || !this.salaryContextLoaded || this.disabledBtn || !this.bonus
      || typeof this.bonus.amount !== NUMBER_TYPE || !isFinite(this.bonus.amount)
      || Math.abs(this.bonus.amount) > MAX_BONUS_AMOUNT
      || Math.round(this.bonus.amount * BONUS_MONEY_FACTOR) / BONUS_MONEY_FACTOR !== this.bonus.amount
      || !this.bonusForever && (!Number.isInteger(this.bonus.months)
        || this.bonus.months < FIRST_BONUS_MONTH || this.bonus.months > MAX_BONUS_MONTHS);
  }

  private saveBonus(): void {
    if (!this.bonusChanged) {
      this.handleSaved();
      return;
    }
    const request = Object.assign({}, this.bonus, {months: this.bonusForever ? null : this.bonus.months});
    this.disabledBtn = true;
    this.bonusErrorKey = null;
    this.subscriptions.add(this.adminService.updateMonthlyBonus(this.id, request).subscribe(
      () => this.handleSaved(),
      error => {
        this.disabledBtn = false;
        this.bonusErrorKey = error.status === BONUS_CONFLICT_STATUS ? BONUS_CONFLICT_KEY : BONUS_SAVE_ERROR_KEY;
      }
    ));
  }

  private get bonusChanged(): boolean {
    return !!this.bonus && (!this.initialBonus
      || this.bonus.amount !== this.initialBonus.amount
      || this.bonus.currency !== this.initialBonus.currency
      || (this.bonusForever ? null : this.bonus.months) !== this.initialBonus.months);
  }

  private loadSalaryContext(): void {
    this.isLoading = true;
    this.salaryContextLoaded = false;
    this.subscriptions.add(
      this.adminService.getSalaryContext(this.id).subscribe(
        context => this.applySalaryContext(context),
        () => this.handleLoadFailed()
      )
    );
  }

  private applySalaryContext(context: SalaryContext): void {
    this.value = context.salary;
    this.changeLog = salaryDescriptionText(context.description);
    this.effectiveCurrency = context.effectiveCurrency;
    this.inheritedCurrency = context.inheritedCurrency || context.effectiveCurrency;
    this.inheritCalendarCurrency = !context.payrollCurrencyOverride;
    this.selectedCurrency = context.payrollCurrencyOverride || context.effectiveCurrency;
    this.salaryContextLoaded = true;
    this.isLoading = false;
    this.bonus = context.monthlyBonus ? Object.assign({}, context.monthlyBonus) : null;
    this.bonusForever = !this.bonus || this.bonus.months == null;
    this.initialBonus = this.bonus ? Object.assign({}, this.bonus, {months: this.bonusForever ? null : this.bonus.months}) : null;
  }

  private saveAsAdmin(): void {
    const request: AdminSalaryUpdateRequest = {
      salary: this.value,
      description: this.changeLog || EMPTY_DESCRIPTION,
      payrollCurrencyOverride: this.inheritCalendarCurrency ? undefined : this.selectedCurrency
    };
    this.subscriptions.add(
      this.adminService.updateAdminSalary(this.id, request).subscribe(
        () => this.saveBonus(),
        () => this.handleSaveFailed()
      )
    );
  }

  private saveAsHead(): void {
    const request: AdminSalaryUpdateRequest = {
      salary: this.value,
      description: this.changeLog || EMPTY_DESCRIPTION,
      payrollCurrencyOverride: this.inheritCalendarCurrency ? null : this.selectedCurrency
    };
    this.subscriptions.add(
      this.adminService.updateHeadSalary(this.id, request).subscribe(
        () => this.saveBonus(),
        () => this.handleSaveFailed()
      )
    );
  }

  private handleSaved(): void {
    this.disabledBtn = false;
    this.activeModal.close(AppConstants.MODAL_RESULT.OK);
  }

  private handleSaveFailed(): void {
    this.disabledBtn = false;
    this.errorInfo = true;
  }

  private handleLoadFailed(): void {
    this.isLoading = false;
    this.errorInfo = true;
  }
}



// WEBPACK FOOTER //
// C:/work/internal-frontend/src/app/content-page/views/view-user-profile/change-salary/change-salary.component.ts